import React from 'react';

import ReCAPTCHA from 'react-google-recaptcha';

import { Block } from './Block.jsx';
import { Builder, EditorContainer } from '../layout/Builder.jsx';
import { Clickable, Hoverable, Wrapper as ButtonWrapper, withBlockTypeConfigPropsV2 } from './Button.jsx';
import Defaults from '../layout/Defaults.jsx';
import Utilities from './Utilities.jsx';

// region BUTTON

function _FormButtonComponent(props) {
    const formFieldProps = {};
    if (props.onFormFieldProps) {
        props.onFormFieldProps({ config: props.blockConfig, type: props.blockType }, formFieldProps);
    }
    let ButtonComponent = Clickable;
    if (props.hoverProps != undefined) {
        ButtonComponent = Hoverable;
    }
    const style = {}
    Utilities.copyObject(props.style, style);
    if (props.builder || props.disabled) {
        style.pointerEvents = 'none';
    }
    let className = 'btn btn--whitelabel btn--form btn--custom2 richText';
    if (props.className) {
        className += ` ${props.className}`;
    }
    if (formFieldProps.inPopup) {
        className += ' btn--form--popup';
    }
    switch (formFieldProps.formState) {
        case 'processing':
            className += ' btn--processing';
            break;
        case 'success':
            className += ' btn--success';
            break;
    }
    const submissionStatus = '<span class="submission-status"></span>';
    const injectSubmissionStatus = (design) => {
        let index = design.indexOf('<p');
        if (index >= 0) {
            index = design.indexOf('>', index);
            if (index >= 0) {
                return design.substring(0, index + 1) + submissionStatus + design.substring(index + 1);
            }
        }
        return design;
    }
    let design = submissionStatus;
    if (props.design) {
        design = injectSubmissionStatus(props.design);
    }
    if (props.hoverProps && props.hoverProps.design) {
        props.hoverProps.design = injectSubmissionStatus(props.hoverProps.design);
    }
    return (
        <ButtonWrapper {...props}>
            <ButtonComponent
                className={className}
                design={design}
                hoverProps={props.hoverProps}
                style={style}
                type="button"
                onClick={formFieldProps.onClick || props.onButtonClick} />
        </ButtonWrapper>
    )
}
const FormButtonComponentV1 = withBlockTypeConfigPropsV2(_FormButtonComponent);
const FormButtonComponentV2 = withBlockTypeConfigPropsV2(_FormButtonComponent, undefined, true);
const FormButton = { V1: FormButtonComponentV1, V2: FormButtonComponentV2 };

// region CAPTCHA

const recaptchaSiteKey = '6LdE-D4UAAAAAPjfhk3coehRBlKmmJZEAJFQGO1C';
const Recap = (props) => <div className="g-recaptcha" data-sitekey={recaptchaSiteKey} {...props}></div>

function RecapAsync(props) {
    const [rendered, setRendered] = React.useState(false);
    React.useEffect(() => {
        if (!grecaptcha || !grecaptcha.render) {
            return;
        }
        setRendered(true);
    }, [grecaptcha]);
    React.useEffect(() => {
        if (!rendered) {
            return;
        }
        setTimeout(() => {
            const options = props.options || {};
            options.sitekey = recaptchaSiteKey;
            const recaptchaId = grecaptcha.render(props.id, options)
            if (props.onRecaptchaId) {
                props.onRecaptchaId(recaptchaId);
            }
        }, 100);
    }, [rendered]);

    let className;
    if (rendered) {
        className = 'g-recaptcha';
    }
    return <div className={className} id={props.id} options={props.options} />
}

// region CONTAINER

function FormContainerComponent(props) {
    let blocks = [];
    if (props.contentConfig && props.contentConfig.desktop && props.contentConfig.desktop.blocks) {
        blocks = props.contentConfig.desktop.blocks
    }

    let className = 'form form--whitelabel';
    if (props.className) {
        className += ` ${props.className}`;
    }
    let disabled = props.builder == true;
    switch (props.formData.formState) {
        case 'processing':
            className += ' form--loading';
        // Intentional fallthrough
        case 'success':
            disabled = true;
            break;
    }
    const layoutStyles = Block.mapLayoutStyleConfigPropsWithPageKit({}, props, undefined, (propName, propValue, propUnit) => {
        switch (propName) {
            case 'design':
                return true;
            default:
                if (props.blockPropsHandler) {
                    return props.blockPropsHandler(propName, propValue, propUnit);
                }
                break;
        }
    });
    if (!props.editor && disabled && props.style) {
        layoutStyles.style.pointerEvents = 'none';
    }
    const handleInputProps = ({ blockConfig, type }, inputProps) => {
        inputProps.disabled = disabled;
        inputProps.errors = props.formData.errors;
        inputProps.name = type;
        inputProps.value = props.formData[type];
        inputProps.onChange = props.onChange;

        if (props.onFormFieldProps) {
            props.onFormFieldProps({ blockConfig, type }, inputProps);
        }
        if (inputProps.placeholderText) {
            inputProps['aria-label'] = inputProps.placeholderText;
        }
    }
    if (!props.inLayout) {
        if (!props.inPopup) {
            className += ' cont--kit__content';
        }
    } else {
        layoutStyles.style.width = '100%';
    }
    let content = (
        <EditorContainer
            {...props}
            backgroundColor={props.onBackgroundColor && props.onBackgroundColor()}
            blocks={blocks}
            blockPropsHandler={() => ({ inPopup: props.inPopup, onFormFieldProps: props.onFormFieldProps, onInputProps: handleInputProps })}
            builderBlockPropsHandler={Builder.handleBlockProps}
            className={className}
            inCell={true}
            isForm={true}
            inLayout={false}
            style={layoutStyles.style} />
    );
    if (layoutStyles.containerStyle) {
        if (props.inLayout) {
            layoutStyles.containerStyle.width = '100%';
        }
        content = <div className="cont--kit__wrapper" style={layoutStyles.containerStyle}>{content}</div>
    }
    return content;
}
const FormContainer = { Component: FormContainerComponent }

// region SUCCESS MESSAGE

function SuccessMessageComponent(props) {
    const formFieldProps = {};
    if (props.onFormFieldProps) {
        props.onFormFieldProps({ config: props.blockConfig, type: props.blockType }, formFieldProps);
    }
    if (!formFieldProps.editor && formFieldProps.formState != 'success') {
        return null;
    }
    let design;
    const style = {};
    if (props.blockConfig && props.blockConfig.block) {
        Block.mapStyleConfigProps(style, props.blockConfig.block, (propName, propValue) => {
            switch (propName) {
                case 'design':
                    design = propValue;
                    return true;
            }
        })
    }
    if (props.content) {
        design = props.content;
    } else if (!design) {
        design = Defaults.Block.SuccessMessage();
    }
    const layoutStyles = Block.mapLayoutStyleConfigPropsWithPageKit(style, props);
    let content = <div className="richText" dangerouslySetInnerHTML={{ __html: design }} style={layoutStyles.style} />
    if (layoutStyles.containerStyle) {
        content = <div style={layoutStyles.containerStyle}>{content}</div>
    }
    return content;
}
const SuccessMessage = { Component: SuccessMessageComponent };

// region VALIDATION

function withSubscriptionLogging(WrappedComponent, method) {
    return function (props) {
        const handleLogSubscription = () => {
            if (window && window.gtag && method) {
                gtag('event', 'subscribe', { 'method': method });
            }
            if (window && window.fbq) {
                fbq('track', 'Lead');
            }
            if (window && window.pintrk) {
                pintrk('track', 'lead', {
                    lead_type: method
                });
            }
        }
        return <WrappedComponent {...props} onLogSubscription={handleLogSubscription}>{props.children}</WrappedComponent>
    }
}
function withValidationV2(WrappedComponent, requiredFields) {
    return class extends React.Component {
        constructor(props) {
            super(props);

            this.state = {};

            this.handleChange = this.handleChange.bind(this);
            this.handleError = this.handleError.bind(this);
            this.handleRecapError = this.handleRecapError.bind(this);
            this.handleRecapExpired = this.handleRecapExpired.bind(this);
            this.handleRecapSuccess = this.handleRecapSuccess.bind(this);
            this.handleReset = this.handleReset.bind(this);
            this.handleSubmit = this.handleSubmit.bind(this);
            this.handleValidate = this.handleValidate.bind(this);

            this.captchaRef = React.createRef();
        }

        componentWillUnmount() {
            if (this.submitRequest) {
                this.submitRequest.abort();
                this.submitRequest = null;
            }
        }

        handleChange(name, value) {
            const _value = value + '';
            const errors = this.state.errors;
            if (errors) {
                errors[name] = _value.length == 0;
            }
            this.setState({
                [name]: value
            });
        }

        handleError(error) {
            this.setState({
                errorMessage: error
            });
        }

        handleRecapError(error) {
            this.handleReset();
            this.handleError(error);
        }

        handleRecapExpired() {
            if (this.state.formState == 'success') {
                this.setState({
                    token: undefined
                });
                return;
            }
            this.handleReset();
            this.handleError('Verification expired.');
        }

        handleRecapSuccess(token) {
            this.setState({
                recapVisible: false
            });
            if (!token) {
                this.handleError('Could not verify. Please try again.');
            } else {
                this.setState({
                    formState: 'processing'
                });
                setTimeout(() => this.setState({ token }), 100);
            }
        }

        handleReset(formState) {
            this.setState({
                formState,
                recapVisible: false,
                token: undefined
            });
        }

        handleSubmit(route, postData, callback) {
            if (this.submitRequest) {
                return;
            }

            this.handleError('')
            this.setState({
                formState: 'processing'
            });

            if (route) {
                this.submitRequest = $.post(
                    route,
                    postData,
                    (responseData) => {
                        if (this.props.onLogSubscription) {
                            this.props.onLogSubscription(this.props, this.state);
                        }
                        this.handleReset('success');
                        if (callback) {
                            callback(responseData);
                        }
                    }).fail((_, textStatus) => {
                        if (textStatus == 'abort') {
                            return;
                        }
                        this.setState({
                            formState: undefined
                        });

                        if (this.captchaRef) {
                            this.captchaRef.current.reset();
                        }

                        this.handleError('There was an error with your request. Please validate your input and try again!');
                    }).always(() => {
                        this.submitRequest = null;
                    });
            } else {
                setTimeout(() => this.handleReset('success'), 1000);
            }
        }

        handleValidate(additionalFields) {
            if (this.state.formState) {
                return;
            }
            let errors = undefined;
            const _checkValidity = (name) => {
                if (!this.state[name]) {
                    if (!errors) {
                        errors = {};
                    }
                    errors[name] = true;
                }
            }
            requiredFields.forEach(_checkValidity)
            if (additionalFields) {
                additionalFields.forEach(_checkValidity);
            }
            this.setState({
                errors,
                recapVisible: errors == undefined
            });
            if (errors == undefined) {
                if (this.props.recaptcha == false) {
                    this.handleRecapSuccess("faketoken");
                } else if (grecaptcha) {
                    this.captchaRef.current.execute();
                }
            }
        }

        render() {
            const handleContent = (content, style, contentProps) => {
                if (contentProps) {
                    Utilities.copyObject(style, contentProps.style);
                }
                return (
                    <>
                        {content}
                        {
                            this.props.recaptcha != false &&
                            <>
                                <div style={{ display: 'none' }}>
                                    This site is protected by reCAPTCHA and the Google
                                    <a href="https://policies.google.com/privacy">Privacy Policy</a> and
                                    <a href="https://policies.google.com/terms">Terms of Service</a> apply.
                                </div>
                                {
                                    !this.props.builder &&
                                    <ReCAPTCHA
                                        ref={this.captchaRef}
                                        size="invisible"
                                        sitekey="6Lct3QIqAAAAAKV2HzkS36bP3wYLDYGxaEwuriXE"
                                        onChange={this.handleRecapSuccess}
                                        onErrored={this.handleRecapError}
                                        onExpired={this.handleRecapExpired}
                                    />
                                }
                            </>
                        }
                    </>
                )
            }
            return (
                <WrappedComponent
                    {...this.props}
                    formData={this.state}
                    onChange={this.handleChange}
                    onContent={handleContent}
                    onError={this.handleError}
                    onReset={this.handleReset}
                    onSubmit={this.handleSubmit}
                    onValidate={this.handleValidate}>
                    {this.props.children}
                </WrappedComponent>
            );
        }
    }
}

function withValidation(WrappedComponent, requiredFields, onSuccess) {
    return class extends React.Component {
        constructor(props) {
            super(props);

            this.state = {};

            this.handleChange = this.handleChange.bind(this);
            this.handleError = this.handleError.bind(this);
            this.handleSubmit = this.handleSubmit.bind(this);
            this.handleValidate = this.handleValidate.bind(this);
        }

        componentWillUnmount() {
            if (this.submitRequest) {
                this.submitRequest.abort();
                this.submitRequest = null;
            }
        }

        handleChange(name, value) {
            const _value = value + '';
            const errors = this.state.errors;
            if (errors) {
                errors[name] = _value.length == 0;
            }
            this.setState({
                [name]: value
            });
        }

        handleError(error) {
            alert(error);
        }

        handleSubmit(route, postData) {
            if (this.submitRequest) {
                return;
            }
            this.setState({
                formState: 'processing'
            });
            if (route) {
                this.submitRequest = $.post(
                    route,
                    postData,
                    () => {
                        let callback = typeof onSuccess !== 'undefined' ? onSuccess : (props, state) => {
                            if (this.props.onLogSubscription) {
                                this.props.onLogSubscription(props, state);
                            }
                        };
                        this.setState({
                            formState: 'success'
                        }, () => callback(this.props, this.state));
                    }).fail((_, textStatus) => {
                        if (textStatus == 'abort') {
                            return;
                        }
                        this.setState({
                            formState: undefined
                        });
                        this.handleError('There was an error with your request. Please validate your input and try again!');
                    }).always(() => {
                        this.submitRequest = null;
                    });
            } else {
                setTimeout(() => {
                    this.setState({
                        formState: 'success'
                    });
                }, 1000);
            }
        }

        handleValidate(recatpchaId) {
            let errors;
            requiredFields.forEach((name) => {
                if (!this.state[name]) {
                    if (!errors) {
                        errors = {};
                    }
                    errors[name] = true;
                }
            });
            this.setState({
                errors
            });
            if (errors) {
                return null;
            }
            if (!recatpchaId) {
                return true;
            }
            let recap;
            if (recatpchaId) {
                let temp = document.getElementById(recatpchaId);
                temp = temp.getElementsByClassName('g-recaptcha-response');
                if (temp && temp.length > 0) {
                    recap = temp[0];
                }
            }
            if (!recap) {
                const temp = document.getElementsByName('g-recaptcha-response');
                if (temp && temp.length) {
                    recap = temp[0];
                }
            }
            if (!recap || !recap.value) {
                this.handleError('Are you a robot?');
                return null;
            }
            return recap.value;
        }

        render() {
            const buttonClassName = this.state.formState != undefined ? ` btn--${this.state.formState}` : '';
            const disabled = this.props.builder == true || this.props.disabled == true || this.state.formState != undefined;
            return (
                <WrappedComponent
                    {...this.props}
                    disabled={disabled}
                    buttonClassName={buttonClassName}
                    formData={this.state}
                    onChange={this.handleChange}
                    onSubmit={this.handleSubmit}
                    onValidate={this.handleValidate}>
                    {this.props.children}
                </WrappedComponent>
            );
        }
    }
}

export {
    FormButton,
    FormContainer,
    Recap,
    RecapAsync,
    SuccessMessage,
    withSubscriptionLogging,
    withValidation,
    withValidationV2
};